<template>
  <div style="position:absolute;top:0;left:0;right:0;bottom:0;" class="layout-column">
    <div class="layout-column flex ma-1"
         style="border-radius:4px;position:relative;"
         v-bind:style="{background: compColor.contentFrame}">

      <div v-bind:style="{'z-index': '4'}" class="layout-row layout-align-center-center pa-1">
        <div class="layout-row">
          <lx-btn small classes="mr-1"
                  @click="getDataForTable()"
                  icon="refresh"
                  tooltip="Ansicht aktualisieren"
                  label="Aktualisieren"
                  hide-label></lx-btn>
          <lx-btn small classes="mr-1"
                  @click="addTabToStore({routeName: 'PatientNew', options: {icon: 'mdi-new-box', headline: 'Patient'}})"
                  icon="mdi-account-plus"
                  tooltip="Neuen Patienten anlegen"
                  label="Patient anlegen"
                  hide-label></lx-btn>
          <lx-btn small classes="mr-1"
                  @click.stop="addTabToStore({routeName: 'Blacklist', options: {icon: 'mdi-ghost', headline: 'Gesperrte Patienten'}})"
                  icon="mdi-ghost"
                  tooltip="Liste gesperrter Patienten bearbeiten"
                  hide-label></lx-btn>
          <v-badge
            :color="compColor.primeColor"
            v-if="userIsPhysicianOrEmployee"
            :content="JSON.stringify(countReportableForms)"
            offset-y="16"
            offset-x="20"
            style="z-index: 10"
            :dark="compColor.isInDarkMode"
            overlap
          >
            <lx-btn small classes="mr-1"
                    v-if="userIsPhysicianOrEmployee"
                    :disabled="tableObj.loading || !countReportableForms"
                    @click="showReportDialog = true"
                    icon="mdi-email-send"
                    tooltip="Neue Meldung erstellen (nur aktiv, wenn meldefähige Formulare vorhanden)"
                    label="Meldung erstellen"
                    hide-label></lx-btn>
          </v-badge>
        </div>

        <div class="flex layout-row mr-1 ml-3">
          <lx-search-field
            placeholder="Suchbegriff (Nachname, Vorname, AIS ID, Tumor ID)"
            dense
            v-model="tableObj.filterValue"
            :data-method="getDataForTable"
          ></lx-search-field>
        </div>

        <div class="layout-row">
          <v-select
            :color="compColor.primeColor"
            placeholder="Suche filtern"
            v-model="selectedListEntry"
            :items="listSelectionList"
            hide-no-data
            hide-details
            outlined
            clearable
            :background-color="compColor.inputs"
            dense
            :dark="compColor.isInDarkMode"
            item-disabled="disabled"
            class="blue-grey--text ma-0"
            :menu-props="{ bottom: true, offsetY: true }"
            @change="getDataForTable()">
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-on="on"
                           :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                <v-list-item-content class="py-0">
                  <label style="cursor:pointer;"
                         :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                         class="lighten-4 text--darken-2">{{ item.text }}</label>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-select>
        </div>

        <v-tooltip bottom open-delay="1000">
          <template v-slot:activator="{ on }">
            <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-center mx-5"
                 style="cursor: pointer;">
              <v-icon :color="compColor.prime" medium>info</v-icon>
              <span class="ml-2 body-2" :style="{color: compColor.primeColor}">{{ $t('FORMS_DIVERSE.LABEL_INFO_STATUS_COLOR') }}</span>
            </div>
          </template>
          <legend-table legendKey="patientList"></legend-table>
        </v-tooltip>
      </div>

      <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

      <div class="flex layout-column lx-delivery-background pa-1" style="overflow-y: hidden">

        <div class="elevation-3 flex layout-column pa-1" style="border-radius: 6px;"
             :style="{'background': compColor.lxExpansion.background}">
          <div style="border-radius: 6px;border: 1px solid rgba(0,0,0,0.1);" class="flex layout-column" ref="PatientsList">
            <lx-data-table
              v-model="tableObj.selection"
              tableName="PatientsList"
              :items="items"
              :stayAtScrollPosition="true"
              :headers="tableObj.headers"
              :loading="tableObj.loading"
              :tableOptions="tableObj.options"
              :hideContextFilter="true"
              @changeOptions="changeTableOptions"
              @rowClicked="openPatientDetails"
              @openContextOnRowClick="openContextOnRowClick">

              <template #masterdetail="{ item }">
                <div class="py-4 px-6" :style="{'max-width': windowW + 'px !important'}">
                  <diagnoses-list :patientId="item._id"></diagnoses-list>
                </div>
              </template>

              <template #cellrenderer="{ value }">
                <div v-if="value.f.renderer === 'legend'" style="padding:1px;height:35px;">
                  <div id="ghostIcon" class="layout-row layout-align-center-center" style="border-radius:3px;height:33px;"
                       :class="['legend-color-' + value.i.color]">
                    <v-avatar color="#ffffffaa" size="26" v-if="value.i.is_permanent_ignored || (value.i.is_ignored_until && new Date(value.i.is_ignored_until).getTime() >= (new Date()).setHours(12,0,0,0))">
                      <v-icon class="icon1" dense color="3A3B3C">mdi-ghost</v-icon>
                    </v-avatar>
                  </div>
                </div>

                <div v-if="value.f.renderer === 'delete' && value.i.erasable"
                     style="cursor:pointer;"
                     class="layout-align-center-start">
                  <v-icon :color="compColor.primeColor" @click="openDeleteDialog(value.i, $event)">mdi-delete-forever</v-icon>
                </div>

                <div v-if="value.f.renderer === 'gender_patient'"
                     class="layout-row layout-align-center-center">
                  <v-icon color="pink accent-2" v-if="value.i.gender === 'W'">mdi-gender-female</v-icon>
                  <v-icon color="blue lighten-1" v-if="value.i.gender === 'M'">mdi-gender-male</v-icon>
                  <v-icon color="grey lighten-1" v-if="value.i.gender === 'U'">mdi-gender-male-female-variant</v-icon>
                  <v-icon color="grey lighten-1" v-if="value.i.gender === 'D'">mdi-gender-transgender</v-icon>
                  <v-icon color="grey lighten-1" v-if="value.i.gender === 'X'">mdi-gender-non-binary</v-icon>
                </div>
              </template>
            </lx-data-table>
          </div>
        </div>
      </div>

      <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

      <div class="layout-row pa-0">
        <lx-pagination :page-name="'PatientsList'"
                       :page-options="pagingOptions"
                       :page-change="getDataForTable">
        </lx-pagination>
      </div>
    </div>

    <report-dialog
      :showReportDialog="showReportDialog"
      @set-show-report-dialog="setShowReportDialog">
    </report-dialog>

    <popup-blacklist-duration
        :show-duration-popup="showDurationDialog"
        @set-show-blacklist-duration-dialog="addPatientToBlacklist">
    </popup-blacklist-duration>
  </div>
</template>

<script>
import LxPagination from '../../components/lx_pagination/LxPagination'
import diagnosesList from './DiagnosesList'
import ReportDialog from '../../components/documentation/ReportDialog'
import legendTable from '../../components/legend_table/legendTable'
import PopupBlacklistDuration from '../../components/patients/PopupBlacklistDuration.vue'

import utils from "../../common/utils"

export default {
  name: 'PatientsList',
  props: ['tabid'],
  mixins: [utils],
  data () {
    return {
      windowW: window.innerWidth,
      tableObj: {
        loading: false, // benutzt während des GetData, um eine Ladeanimation einzublenden
        options: {
          sortBy: ['lastname'],
          sortDesc: [false],
          getClassForItem: (row) => {
            if (row.isExpanded) {
              return 'row-bold'
            }
            return ''
          }
        },
        filterValue: null,
        selection: null,
        headers: [
          {
            text: '',
            width: '20px',
            renderer: 'delete'
          },
          {
            text: 'Nachname',
            value: 'lastname',
            width: '170px',
            sortable: true
          },
          {
            text: 'Vorname',
            value: 'firstname',
            width: '150px',
            class: 'pinned',
            sortable: true
          },
          {
            text: 'AIS ID',
            value: 'ais_id',
            width: '80px',
            class: 'pinned',
            sortable: true
          },
          {
            text: 'Geburtsdatum',
            value: 'birthday',
            width: '90px',
            renderer: 'date',
            sortable: true
          },
          {
            text: 'Geschlecht',
            value: 'gender',
            width: '80px',
            class: 'text-center',
            renderer: 'gender_patient'
          },
          {
            text: 'Fortschritt',
            value: 'color',
            width: '200px',
            class: 'text-center',
            renderer: 'legend'
          },
          {
            text: 'Zuletzt bearbeitet am',
            value: 'last_modified',
            width: '130px',
            renderer: 'date',
            sortable: true
          },
          {
            text: '',
            value: 'data-table-expand',
            sticky: 'right',
            width: '50px'
          }
        ]
      },
      pagingOptions: {
        limit: 50,
        skip: 0,
        count: 0
      },
      items: [],
      selectedListEntry: null,
      listSelectionList: [
        { value: 'Yellow', text: this.$t('COMPONENT_PATIENT.PATIENT_IN_PROGRESS') }
      ],
      deleteDialog: false,
      authUserToDelete: null,
      deleteDialogTitle: this.$t('CONFIRM_DELETE_DIALOG.HEADER'),
      deleteDialogMessage: this.$t('CONFIRM_DELETE_DIALOG.MESSAGE_DELETE_FORM'),
      deleteDialogOpts: {
        ok: this.$t('buttons.delete.value'),
        cancel: this.$t('buttons.cancel.value')
      },
      showReportDialog: false,
      isLoadingData: false,
      countReportableForms: 0,
      showDurationDialog: false,
      selectedPatientForIgnore: null
    }
  },
  components: {
    ReportDialog,
    LxPagination,
    diagnosesList,
    legendTable,
    PopupBlacklistDuration
  },
  created () {
    this.getDataForTable()
  },
  mounted () {
    const tC = this.$refs.PatientsList
    this.windowW = tC.clientWidth - 20

    window.addEventListener('resize',() => {
      this.windowW = tC.clientWidth - 20
    })

    if (typeof this.createCategory === "function" && typeof this.addEntryToMenu === "function") {
      this.createCategory('PatientListTable')
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', null)
  },
  methods: {
    /**
     * Open popup to give user possibility to decide if patients are really to be deleted
     */
    openDeleteDialog (params, event) {
      if (event) {
        event.stopPropagation()
      }

      this.DialogConfirm(
        this.deleteDialogTitle,
        this.deleteDialogMessage,
        this.deleteDialogOpts)
        .then(() => {
          if (params && params._id) {
            this.$http.delete('/patients/' + params._id)
              .then(res => {
                this.removeTabIfDatasetWasDeleted({
                  routeName: 'PatientDetails',
                  routeParams: { patientId: params._id }
                })

                this.NotifySuccess(res, 'Die Daten von "' + params.lastname + ', ' + params.firstname + '" wurden erfolgreich gelöscht')
                this.getDataForTable()
              })
              .catch(this.NotifyError)
          }
        })
        .catch(() => {
        })
    },

    /**
     * Open special context menu for selected patient
     *
     * @param event The opening event
     * @param item The row value
     */
    openContextOnRowClick (event, item) {
      if (item) {
        this.addEntryToMenu({
          type: 'PatientListTable',
          value: [
            {
              icon: true,
              iconName: 'mdi-ghost',
              title: 'Zur Liste gesperrter Patienten hinzufügen',
              action: this.openIgnoreDurationDialog,
              disabled: item.is_permanent_ignored || (item.is_ignored_until && new Date(item.is_ignored_until).getTime() >= (new Date()).setHours(12,0,0)) || false
            },
            {
              icon: true,
              iconName: 'mdi-delete',
              title: 'Endgültig Löschen',
              action: this.openDeleteDialog,
              disabled: !item.erasable || false
            }
          ]
        })
        this.$lxContext.open(event, 'PatientListTable', item)
      }
    },

    /**
     * Open dialog to select duration of ignoring for import
     *
     * @param params The data of selected patient
     */
    openIgnoreDurationDialog (params) {
      if (params?._id) {
        this.selectedPatientForIgnore = params._id
        this.showDurationDialog = !this.showDurationDialog
      } else {
        this.selectedPatientForIgnore = null
      }
    },

    /**
     * Blacklist selected patient
     *
     * @param params The row value
     */
    addPatientToBlacklist (params) {
      if (this.selectedPatientForIgnore && params) {
        if (this.tableObj.loading || this.isLoadingData) return
        this.isLoadingData = true

        this.$http.put('/patients/blacklist',{id: this.selectedPatientForIgnore, type: params.type, date: params.date})
          .then(() => {
            this.isLoadingData = false
            this.getDataForTable()
            this.NotifySystem(this.$t('BLACKLIST.NOTIFICATIONS.BLACKLIST_SUCCESS'))

            this.showDurationDialog = false
            this.selectedPatientForIgnore = null
          })
          .catch(() => {
            this.NotifyError(this.$t('BLACKLIST.NOTIFICATIONS.BLACKLIST_ERROR'))
          })
          .finally(() => {
            this.isLoadingData = false
          })
      } else {
        this.showDurationDialog = false
        this.selectedPatientForIgnore = null
      }
    },

    /**
     * Open or close modal with report dialog
     */
    setShowReportDialog () {
      this.showReportDialog = !this.showReportDialog

      if (!this.showReportDialog) {
        this.getDataForTable()
      }
    },

    /**
     * React on changes of options in lx-data-table, set table-options in component and call getDataForTable
     */
    changeTableOptions (value) {
      if (value) {
        this.tableObj.options = value
      }
      this.getDataForTable()
    },

    /**
     * Open details of selected patient in a separate tab
     */
    openPatientDetails (params) {
      if (params?._id) {
        this.addTabToStore({
          routeName: 'PatientDetails',
          routeParams: { patientId: params._id },
          options: {
            headline: params.lastname + ', ' + params.firstname
          }
        })
      }
    },

    /**
     * get list of all linked patients
     */
    getDataForTable (options) {
      if (this.tableObj.loading) return
      this.tableObj.loading = true

      if (options && typeof options.skip === 'number') {
        this.pagingOptions.skip = options.skip
      }
      if (options && options.limit) {
        this.pagingOptions.limit = options.limit
      }

      let opts = {
        skip: this.pagingOptions.skip,
        limit: this.pagingOptions.limit
      }

      if (this.tableObj.options && this.tableObj.options.sortBy && this.tableObj.options.sortBy.length > 0) {
        opts.sort = {}
        opts.sort[this.tableObj.options.sortBy[0]] = this.tableObj.options.sortDesc[0] ? -1 : 1
      }

      let query = {}

      // set search value only if filter value has content, else clear filter value (e.g. string has only spaces)
      if (this.tableObj.filterValue && this.tableObj.filterValue.replace(/\s/g, '').length > 0) {
        query.searchValue = this.clearStringForRegexSearch(this.tableObj.filterValue)
      } else {
        this.tableObj.filterValue = null
      }

      let url = '/patients'

      if (this.selectedListEntry && this.selectedListEntry.toString().toLowerCase() === 'yellow') {
        url = '/patients/inProgress'
      }

      this.$http.get(url + '?config={"opts":' + JSON.stringify(opts) + ', "query": ' + JSON.stringify(query) + '}')
        .then(res => {
          if (res.data) {
            this.items = res.data.result || []
            this.countReportableForms = res.data.countReportableForms || 0

            this.pagingOptions.count = res.data.count || 0
          }
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.tableObj.loading = false
        })
    }
  },
  computed: {
    userIsPhysician () {
      return this.authStore.getters.userIsPhysician
    },
    userIsPhysicianOrEmployee () {
      return this.authStore.getters.userIsPhysicianOrEmployee
    }
  },
  watch: {
    '$route' (to) {
      if (to.name === 'PatientsList') {
        this.getDataForTable()
      }
    }
  }
}
</script>

<style lang="scss">
.v-badge__badge {
  font-size: 10px !important;
  height: auto !important;
}

#ghostIcon .v-avatar .theme--dark.v-icon {
    color: #3A3B3C !important;
  }
</style>
